import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Grid, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import FormInput, { DateInput, SSNInput } from "../FormInput";
import SelectInput from "../SelectInput";
import MultiSelectInput from "../MultiSelectInput";
import { statesList } from "../../lib/constants";
import AgentService from "../../services/agent.service";
import fsdService from "../../services/fsd.service";
import { useAuth } from "../../hooks/useAuth";
import { useBranding } from "../../hooks/useBranding";

// #50/#47 — field-driven onboarding. We only validate (and only render) the
// required fields the agent is actually MISSING, per the server-computed list
// (user.agentMissingFields). Payment sub-fields are validated conditionally on
// the chosen payment method whenever payment is in play.
const dobSchema = Yup.date()
  .transform((val, original, context) => {
    if (context.isType(val)) return val;
    if (original == null || original === "" || /^[_/]+$/.test(original)) {
      return undefined;
    }
    const parsed = dayjs(original, "MM/DD/YYYY");
    if (!parsed.isValid()) return new Date("");
    return parsed.toDate();
  })
  .typeError("Date of birth is required")
  .required("Date of birth is required");

const buildValidationSchema = (missing) => {
  const has = (k) => missing.includes(k);
  const shape = {
    // Always-optional extras.
    preferredName: Yup.string(),
    commissionEmail: Yup.string().email("Must be a valid email").optional(),
    // Payment sub-fields: required only for the matching payment method, and only
    // ever relevant once payment is being collected.
    paymentCorporation: Yup.string().when("payment", { is: "corporation", then: (s) => s.required("Corporation name is required") }),
    paymentNpn: Yup.string().when("payment", { is: "corporation", then: (s) => s.required("Corporation NPN is required") }),
    paymentTaxId: Yup.string().when("payment", { is: "corporation", then: (s) => s.required("Tax ID is required") }),
    paymentUpline: Yup.string().when("payment", { is: "upline", then: (s) => s.required("Upline name is required") }),
  };
  if (has("npn")) shape.npn = Yup.string().required("NPN is required");
  if (has("firstName")) shape.firstName = Yup.string().required("First name is required");
  if (has("lastName")) shape.lastName = Yup.string().required("Last name is required");
  if (has("email")) shape.email = Yup.string().email("Must be a valid email").required("Email is required");
  if (has("ssn")) shape.ssn = Yup.string().required("Social Security Number is required").matches(/^\d{3}-?\d{2}-?\d{4}$/, { message: "Must be a SSN of the form XXX-YY-ZZZZ" });
  if (has("dob")) shape.dob = dobSchema;
  if (has("phone")) shape.phone = Yup.string().required("Phone is required");
  if (has("address")) shape.address = Yup.string().required("Address is required");
  if (has("city")) shape.city = Yup.string().required("City is required");
  if (has("state")) shape.state = Yup.string().required("State is required");
  if (has("zip")) shape.zip = Yup.string().required("Zip is required");
  if (has("licensedStates")) shape.licensedStates = Yup.array().of(Yup.string()).min(1, "Select at least one state you're licensed in");
  if (has("fsd")) shape.fsd = Yup.string().required("Please select who recommended you for Applied GA");
  if (has("payment")) shape.payment = Yup.string().required("Payment method is required");
  return Yup.object().shape(shape);
};

// Payment options offered at onboarding (same as the old contract Payment step:
// paid to me / to my corporation-LLC / to my LOA/AOC).
const onboardingPaymentOptions = [
  { id: "me", name: "Commission payable to me" },
  { id: "corporation", name: "Make payable to my corporation/LLC" },
  { id: "upline", name: "Make payment to my LOA/AOC" },
];

const Display = ({ label, value }) => (
  <div style={{ marginBottom: 12 }}>
    <Typography variant="caption" color="textSecondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1">{value || "-"}</Typography>
  </div>
);

// #57 - targetPrefill/targetMissing/onSaved switch this into the principal
// on-behalf variant: the SAME screen the agent would have completed, but for a
// hierarchy agent (fields from their prefill, saved to their record).
const Onboarding = ({ targetPrefill = null, targetMissing = null, onSaved = null }) => {
  const navigate = useNavigate();
  const { user, refetch, agentMissingFields } = useAuth();
  const { branding } = useBranding();
  const onBehalf = !!targetPrefill;
  const agent = onBehalf ? targetPrefill : user?.agent;
  // #50/#47 — render only the required fields this account is missing.
  const missing = onBehalf ? targetMissing || [] : agentMissingFields(user);
  const show = (k) => missing.includes(k);
  const showPayment = missing.some((k) => k.startsWith("payment"));
  const [fsdOptions, setFsdOptions] = useState([]);
  const [message, setMessage] = useState("");
  const [confirming, setConfirming] = useState(false);

  // #2 — a pending signup match shows "Is this you?" before regular onboarding.
  // Never in the on-behalf variant: match state belongs to the account itself.
  const pendingMatch =
    !onBehalf && user?.agentMatchPending ? user?.pendingMatch : null;
  const supportEmail = branding?.contact?.email;

  const handleConfirmMatch = async (confirmed) => {
    setMessage("");
    setConfirming(true);
    try {
      await AgentService.confirmMatch(confirmed);
      // Bust the cached user so refetch re-reads the fresh session.
      localStorage.removeItem("user");
      await refetch();
      if (confirmed) {
        navigate("/"); // existing agent: skip the rest, go to the dashboard
      }
      // if not confirmed, refetch flips agentMatchRejected -> support screen
    } catch (error) {
      setMessage(
        error.response?.data?.message || error.message || error.toString()
      );
    } finally {
      setConfirming(false);
    }
  };

  const redirect =
    new URLSearchParams(window.location.search).get("redirect") || "/";

  useEffect(() => {
    let active = true;
    fsdService.list().then((fsds) => {
      if (active) setFsdOptions(fsds.map((name) => ({ id: name, name })));
    });
    return () => {
      active = false;
    };
  }, []);

  const onSubmit = async (values, { setSubmitting }) => {
    setMessage("");
    // #57 - on-behalf: save to the TARGET agent's record, then hand control
    // back to the flow that sent us here (no session/cache involvement).
    if (onBehalf) {
      try {
        await AgentService.updateProfileFor(agent.id, values);
        onSaved && onSaved();
      } catch (error) {
        setMessage(
          error.response?.data?.message || error.message || error.toString()
        );
        setSubmitting(false);
      }
      return;
    }
    try {
      await AgentService.updateProfile(values);
      // Bust the cached (pre-onboarding) user so refetch re-reads the fresh
      // session (with onboardingCompletedAt); auth.service caches it for a week.
      localStorage.removeItem("user");
      await refetch();
      navigate(decodeURIComponent(redirect));
    } catch (error) {
      setMessage(
        error.response?.data?.message || error.message || error.toString()
      );
      setSubmitting(false);
    }
  };

  // #2 — "Not me" dead-ends to support (we won't guess who they are).
  if (!onBehalf && user?.agentMatchRejected) {
    return (
      <div className="onboarding-form">
        <Typography variant="h5" sx={{ mb: 1 }}>
          We couldn't verify your account
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Thanks. Since this isn't your record, we can't automatically link your
          account. Please reach out{" "}
          {supportEmail ? (
            <>
              at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
            </>
          ) : (
            "to our support team"
          )}{" "}
          and we'll get you set up.
        </Typography>
      </div>
    );
  }

  // #2 — "Is this you?": confirm the matched existing agent, or say it's not you.
  if (pendingMatch) {
    const r = pendingMatch.reveal || {};
    return (
      <div className="onboarding-form">
        <Typography variant="h5" sx={{ mb: 1 }}>
          Is this you?
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          We found an existing agent record that matches your details. If this is
          you, we'll link your account and you can skip the extra setup.
        </Typography>
        {message && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <Display label="Name" value={pendingMatch.name} />
          </Grid>
          {r.npnLast4 && (
            <Grid item xs={12}>
              <Display label="NPN" value={`•••• ${r.npnLast4}`} />
            </Grid>
          )}
          {r.ssnLast4 && (
            <Grid item xs={12}>
              <Display label="SSN" value={`•••-••-${r.ssnLast4}`} />
            </Grid>
          )}
          {r.emailPartial && (
            <Grid item xs={12}>
              <Display label="E-Mail" value={r.emailPartial} />
            </Grid>
          )}
        </Grid>
        <Button
          fullWidth
          variant="contained"
          disabled={confirming}
          onClick={() => handleConfirmMatch(true)}
          sx={{ mb: 1 }}
        >
          Yes, that's me
        </Button>
        <Button
          fullWidth
          variant="outlined"
          disabled={confirming}
          onClick={() => handleConfirmMatch(false)}
        >
          No, this isn't me
        </Button>
      </div>
    );
  }

  return (
    <div className="onboarding-form">
      <Typography variant="h5" sx={{ mb: 1 }}>
        {onBehalf
          ? `Complete ${[agent?.firstName, agent?.lastName].filter(Boolean).join(" ") || "the agent"}'s profile`
          : "Complete your profile"}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {onBehalf
          ? "A few required details are missing from this agent's record. Fill them in once and the request can proceed."
          : "We need a few details before you can submit requests. You only have to do this once."}
      </Typography>
      {message && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {/* Identity already on file (shown read-only for the fields we have). */}
      {(agent?.npn || agent?.firstName || agent?.lastName || agent?.email) && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {onBehalf ? "Information on file" : "Your information on file"}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            {agent?.npn && (
              <Grid item xs={12}>
                <Display label="Individual NPN" value={agent.npn} />
              </Grid>
            )}
            {agent?.firstName && (
              <Grid item xs={6}>
                <Display label="First Name" value={agent.firstName} />
              </Grid>
            )}
            {agent?.lastName && (
              <Grid item xs={6}>
                <Display label="Last Name" value={agent.lastName} />
              </Grid>
            )}
            {agent?.email && (
              <Grid item xs={6}>
                <Display label="E-Mail" value={agent.email} />
              </Grid>
            )}
          </Grid>
        </>
      )}

      <Formik
        initialValues={{
          npn: agent?.npn || "",
          firstName: agent?.firstName || "",
          lastName: agent?.lastName || "",
          email: agent?.email || "",
          ssn: "",
          dob: null,
          phone: agent?.phone || "",
          address: agent?.address || "",
          city: agent?.city || "",
          state: agent?.state || "",
          zip: agent?.zip || "",
          licensedStates: agent?.licensedStates || [],
          fsd: agent?.fsd || "",
          payment: agent?.payment || "",
          paymentCorporation: agent?.paymentCorporation || "",
          paymentNpn: agent?.paymentNpn || "",
          paymentTaxId: agent?.paymentTaxId || "",
          paymentUpline: agent?.paymentUpline || "",
          preferredName: agent?.preferredName || "",
          commissionEmail: "",
        }}
        validationSchema={buildValidationSchema(missing)}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values }) => (
          <Form>
            {/* Identity fields, editable only when missing (e.g. a principal with
                no linked agent record yet — this creates it). */}
            {(show("npn") || show("firstName") || show("lastName") || show("email")) && (
              <Grid container spacing={2}>
                {show("npn") && (
                  <Grid item xs={12}>
                    <Field name="npn" label="Individual NPN" fullWidth component={FormInput} variant="outlined" />
                  </Grid>
                )}
                {show("firstName") && (
                  <Grid item xs={6}>
                    <Field name="firstName" label="First Name" fullWidth component={FormInput} variant="outlined" />
                  </Grid>
                )}
                {show("lastName") && (
                  <Grid item xs={6}>
                    <Field name="lastName" label="Last Name" fullWidth component={FormInput} variant="outlined" />
                  </Grid>
                )}
                {show("email") && (
                  <Grid item xs={12}>
                    <Field name="email" autoComplete="email" label="E-Mail" fullWidth component={FormInput} variant="outlined" />
                  </Grid>
                )}
              </Grid>
            )}
            {show("fsd") && (
              <Field
                name="fsd"
                label="Who recommended you for Applied GA?"
                component={SelectInput}
                variant="outlined"
                options={fsdOptions}
              />
            )}
            {show("licensedStates") && (
              <Field
                name="licensedStates"
                label="What states are you licensed in?"
                component={MultiSelectInput}
                variant="outlined"
                options={statesList}
              />
            )}
            {(show("ssn") || show("dob")) && (
              <Grid container spacing={2}>
                {show("ssn") && (
                  <Grid item xs={6}>
                    <Field name="ssn" autoComplete="ssn" label="Social Security Number" fullWidth component={SSNInput} variant="outlined" />
                  </Grid>
                )}
                {show("dob") && (
                  <Grid item xs={6}>
                    <Field name="dob" autoComplete="bday" label="Date of Birth" variant="outlined" fullWidth component={DateInput} />
                  </Grid>
                )}
              </Grid>
            )}
            {show("phone") && (
              <Field name="phone" autoComplete="tel" label="Phone" fullWidth component={FormInput} variant="outlined" />
            )}
            {show("address") && (
              <Field name="address" autoComplete="street-address" label="Address" fullWidth component={FormInput} variant="outlined" />
            )}
            {(show("city") || show("state") || show("zip")) && (
              <Grid container spacing={2}>
                {show("city") && (
                  <Grid item xs={4}>
                    <Field name="city" autoComplete="address-level2" label="City" fullWidth component={FormInput} variant="outlined" />
                  </Grid>
                )}
                {show("state") && (
                  <Grid item xs={4}>
                    <Field name="state" autoComplete="address-level1" label="State" component={SelectInput} variant="outlined" options={statesList} />
                  </Grid>
                )}
                {show("zip") && (
                  <Grid item xs={4}>
                    <Field name="zip" autoComplete="postal-code" label="Zip" fullWidth component={FormInput} variant="outlined" />
                  </Grid>
                )}
              </Grid>
            )}
            {showPayment && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  How would you like to be paid?
                </Typography>
                <Field
                  name="payment"
                  label="Payment method"
                  component={SelectInput}
                  variant="outlined"
                  options={onboardingPaymentOptions}
                />
                {values.payment === "corporation" && (
                  <>
                    <Field name="paymentCorporation" label="Corporation Name" fullWidth component={FormInput} variant="outlined" />
                    <Field name="paymentNpn" label="Corporation NPN" fullWidth component={FormInput} variant="outlined" />
                    <Field name="paymentTaxId" label="Tax ID" fullWidth component={FormInput} variant="outlined" />
                  </>
                )}
                {values.payment === "upline" && (
                  <Field name="paymentUpline" label="Name of Upline" fullWidth component={FormInput} variant="outlined" />
                )}
              </>
            )}
            <Field
              name="preferredName"
              autoComplete="nickname"
              label="Preferred Name (optional)"
              fullWidth
              component={FormInput}
              variant="outlined"
            />
            <Field
              name="commissionEmail"
              autoComplete="email"
              label="Commission E-Mail (optional)"
              fullWidth
              component={FormInput}
              variant="outlined"
            />
            <Button
              type="submit"
              disabled={isSubmitting}
              fullWidth
              variant="contained"
              sx={{ mt: 1 }}
            >
              Save and continue
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Onboarding;
